import { Button, Form, Input, InputNumber, Modal, Select, Tag, message } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { T_AffiliatePlan, T_AffiliatePlanType, T_AffiliatePlan_PaymentType } from "dtos/affiliate.plan.dto";
import { useEffect } from "react";
import { TbLoader } from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { queryClient } from "utils/queryClient";

const AffiliatePlansItem = ({
  isModalOpen,
  setIsModalOpen,
  mode,
}: {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: string | undefined;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id") || undefined;
  const addAffiliatePlan = ApiService.affiliatePlans.useAddAffiliatePlan();
  const updateAffiliatePlan = ApiService.affiliatePlans.useUpdateAffiliatePlan();
  const affiliatePlansItem = ApiService.affiliatePlans.useGetById(id);
  const [affiliatePlansItemForm] = Form.useForm();
  const affiliatePlansData = affiliatePlansItem.data;

  const type = Form.useWatch("type", affiliatePlansItemForm);
  const payment_type = Form.useWatch("payment_type", affiliatePlansItemForm);

  useEffect(() => {
    affiliatePlansItemForm.setFieldsValue({
      name: affiliatePlansData?.name,
      type: affiliatePlansData?.type,
      payment_type: affiliatePlansData?.payment_type,
      first_amount: affiliatePlansData?.first_amount,
      recurring_amount: affiliatePlansData?.recurring_amount,
      first_percentage: affiliatePlansData?.first_percentage,
      recurring_percentage: affiliatePlansData?.recurring_percentage,
    });
  }, [id, affiliatePlansData, affiliatePlansItemForm]);

  const onFinish = async () => {
    try {
      const formData = await affiliatePlansItemForm.validateFields();
      const updateAffiliatePlanData: Partial<T_AffiliatePlan> = { ...formData };

      console.log(updateAffiliatePlanData);
      if (affiliatePlansData && id) {
        updateAffiliatePlan.mutate(
          { id: affiliatePlansData._id!, body: updateAffiliatePlanData },
          {
            onSuccess: () => {
              message.success("Affiliate plan updated successfully!");
              //   navigate(`/dashboard/affiliate-plans`);
              queryClient.invalidateQueries({ queryKey: ["affiliate-plan/all"] });
              closeModal();
            },
          },
        );
      } else {
        addAffiliatePlan.mutate(formData, {
          onSuccess: () => {
            message.success("Affiliate plan added successfully");
            // navigate("/dashboard/affiliate-plans");
            queryClient.invalidateQueries({ queryKey: ["affiliate-plan/all"] });
            closeModal();
          },
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const closeModal = () => {
    affiliatePlansItemForm.resetFields();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("id");
      params.delete("mode");
      return params;
    });
    setIsModalOpen(false);
  };

  return (
    <Modal
      title={"Create Affiliate Plan"}
      open={isModalOpen}
      onCancel={closeModal}
      footer={
        mode === undefined && [
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="update"
            disabled={updateAffiliatePlan.isPending || addAffiliatePlan.isPending}
            type="primary"
            onClick={onFinish}
            htmlType="submit"
            icon={
              (updateAffiliatePlan.isPending || addAffiliatePlan.isPending) && <TbLoader className="animate-spin" />
            }
          >
            {affiliatePlansData
              ? updateAffiliatePlan.isPending
                ? "Updating plan..."
                : "Update plan"
              : addAffiliatePlan.isPending
                ? "Adding plan..."
                : "Add plan"}
          </Button>,
        ]
      }
    >
      <PageTitle title="Affiliate Plan" pageTitle={[{ title: "Affiliate Plan" }]} />
      <Form layout="vertical" form={affiliatePlansItemForm}>
        <Form.Item<T_AffiliatePlan>
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please fill this input" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="type" label="Plan Type" rules={[{ required: true }]}>
          <Select placeholder="Select Plan Type">
            <Select.Option value={T_AffiliatePlanType.ONETIME}>One Time</Select.Option>
            <Select.Option value={T_AffiliatePlanType.RECURRING}>Recurring</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="payment_type" label="Payment Type" rules={[{ required: true }]}>
          <Select placeholder="Select Payment Type">
            <Select.Option value={T_AffiliatePlan_PaymentType.FIXED}>Fixed</Select.Option>
            <Select.Option value={T_AffiliatePlan_PaymentType.VARIABLE}>Variable</Select.Option>
          </Select>
        </Form.Item>

        {(payment_type === T_AffiliatePlan_PaymentType.FIXED && type === "Recurring") ||
        (payment_type === "Variable" && type === "Recurring") ? (
          <div className="flex gap-3 items-center">
            <Form.Item<T_AffiliatePlan>
              name="first_amount"
              label="First Amount"
              rules={[{ required: false, message: "Please fill this input" }]}
              className="flex-1"
            >
              <InputNumber style={{ width: "100%" }} prefix="$" />
            </Form.Item>
            <Tag bordered={false} className="bg-gray-200 h-fit rounded-full text-xs font-semibold !m-0">
              OR
            </Tag>
            <Form.Item<T_AffiliatePlan>
              name="first_percentage"
              label="First Percentage"
              rules={[{ required: false, message: "Please fill this input" }]}
              className="flex-1"
            >
              <InputNumber style={{ width: "100%" }} prefix="%" />
            </Form.Item>
          </div>
        ) : null}

        {payment_type === "Fixed" && type === "One Time" ? (
          <Form.Item<T_AffiliatePlan>
            name="first_amount"
            label="First Amount"
            rules={[{ required: true, message: "Please fill this input" }]}
          >
            <InputNumber prefix="$" style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {payment_type === "Variable" && type === "One Time" ? (
          <Form.Item<T_AffiliatePlan>
            name="first_percentage"
            label="First Percentage"
            rules={[{ required: true, message: "Please fill this input" }]}
          >
            <InputNumber prefix="%" style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {payment_type === "Fixed" && type === "Recurring" ? (
          <Form.Item<T_AffiliatePlan>
            name="recurring_amount"
            label="Recurring Amount"
            rules={[{ required: true, message: "Please fill this input" }]}
          >
            <InputNumber prefix="$" style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {payment_type === "Variable" && type === "Recurring" ? (
          <Form.Item<T_AffiliatePlan>
            name="recurring_percentage"
            label="Recurring Percentage"
            rules={[{ required: true, message: "Please fill this input" }]}
          >
            <InputNumber prefix="%" style={{ width: "100%" }} />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
};

export default AffiliatePlansItem;
